import { FLOOR_LANGUAGE, resolveLanguage } from '../language';
import type { Language } from '../language';
import { ES } from './es';

/**
 * The one way a word a client reads becomes text on a screen.
 *
 * It mirrors `src/shared/formatDate.ts` deliberately, because the two solve the
 * same problem and the date one is already the thing in this repository that
 * works: one module decides, and a lint rule refuses every other caller. Here
 * the lint rule is in `eslint.config.ts` and refuses bare text under
 * `src/app/client`, `src/app/session` and `src/app/instance`.
 *
 * **Why a catalogue rather than English strings and a translation pass later.**
 * Fourteen client screens are about to be written. Writing them in English and
 * translating afterwards is writing them twice, and the seam is cheapest before
 * the first one rather than after the fourteenth (`client-app-direction`,
 * "The language finding, which is not deferrable").
 *
 * **Missing text is a compile error, not a runtime fallback.** There is no
 * `?? id`, no `?? english`, no empty string: `MessageId` is the floor
 * catalogue's own key type, and every language's catalogue is a
 * `Record<MessageId, string>`. A screen that asks for an id nobody wrote, and a
 * language that is missing an entry, both fail `tsc -b` — which `pnpm build`
 * runs first. That is `client-language`'s "A new screen cannot ship
 * untranslated", enforced rather than remembered, and it is why no English can
 * survive anywhere as a fallback: there is nowhere for it to live.
 */

export type MessageId = keyof typeof ES;

/** A complete set of words for one language. Incomplete is a compile error. */
export type Catalogue = Record<MessageId, string>;

const CATALOGUES: Record<Language, Catalogue> = {
  es: ES,
};

/** Values a message can interpolate: a name, a number, a formatted date. */
export type TextParams = Readonly<Record<string, string | number>>;

const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;

/**
 * One message, in the language this render is in.
 *
 * Placeholders are `{name}` and are substituted by `params`. A placeholder with
 * no value is left exactly as written rather than blanked, so a missing
 * parameter shows up on screen as `{coach}` — visible in a screenshot and in a
 * test — instead of silently producing a sentence with a hole in it.
 */
export function text(id: MessageId, params?: TextParams, language?: Language): string {
  const catalogue = CATALOGUES[language ?? resolveLanguage()];
  const template = catalogue[id];
  if (params === undefined) return template;

  return template.replaceAll(PLACEHOLDER, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Every id in the floor catalogue, for the tests that walk the whole set.
 *
 * `FLOOR_LANGUAGE` is the definition of completeness: no screen may exist in
 * some other language and not in this one (`client-language`, "Client-facing
 * text is rendered in the client's language").
 */
export function messageIds(): MessageId[] {
  return Object.keys(CATALOGUES[FLOOR_LANGUAGE]) as MessageId[];
}
